import { useCallback, useEffect, useRef, useState } from "react";
import { usePostHog } from "@posthog/react";
import { gigsApi } from "../../../lib/gigs";
import {
  captureAdultGigCreateModalClosed,
  captureAdultGigCreateModalOpened,
  captureAdultGigCreateStepCompleted,
  captureAdultGigCreateStepViewed,
  captureAdultGigCreateSubmitted,
  captureAdultGigCreateValidationFailed,
  type AdultGigCreateFlowAction,
  type AdultGigCreateFlowAnalyticsContext,
  type AdultGigCreateFlowEntryPoint,
  type AdultGigCreateFlowMode,
  type AdultGigCreateFlowSource,
} from "../../../lib/analytics";
import type { CreateGigRequest, EditableGigStatus, GigScheduleType, LocationType } from "../../../types/gigTypes";
import type { UsZipLocationResponseDto } from "../../../types/types";
import { gigCategoryOptions } from "../../../constants/gigCategories";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import {
  formatEstimatedDuration,
  getBrowserTimeZone,
  resolveGigTimeZone,
  toZonedIsoDate,
  toIsoTimestamp,
  toMinutePrecision,
  zonedEndOfDay,
  zonedStartOfDay,
} from "../../../utils/DateUtils";
import { resolvePresetScheduleWindow, type PreferredTimePreset, type PresetDueDateRange } from "../../../utils/GigScheduleUtils";
import { useAuth } from "../../../hooks/useAuth";
import { hasRole } from "../../../utils/AuthUtils";
import {
  containsBlockedContactInfo,
  GIG_DESCRIPTION_CONTACT_INFO_BLOCK_MESSAGE,
  GIG_DESCRIPTION_CONTACT_INFO_NOTICE,
} from "../../../utils/ContactInfoUtils";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import cameraIcon from "../../../assets/icons/camera.svg";
import locationPinIcon from "../../../assets/icons/location-pin.svg";
import { calculateMultiTeenGigPricingFromPosterAmountCents, parseUsdInputToCents } from "../../../utils/GigPricingUtils";
import { normalizeTeensRequired } from "../../../utils/GigCrewUtils";
import { formatGigOpportunityDescription, parseGigOpportunityDescription, type GigOpportunityType } from "../../../utils/GigOpportunityUtils";
import Button from "../../ui/Button/Button";
import Combobox from "../../ui/Combobox/Combobox";
import Modal from "../../ui/Modal/Modal";
import InfoTooltip from "../../ui/InfoTooltip/InfoTooltip";
import UsLocationFields from "../../ui/UsLocationFields/UsLocationFields";

export type CreateGigFormValues = {
  title: string;
  description: string;
  requiresEquipment: boolean;
  equipmentDescription: string | null;
  category: string;
  locationType: LocationType;
  city: string;
  state: string;
  zip: string;
  timeZone?: string | null;
  startTime: string | null;
  endTime: string | null;
  scheduleType: GigScheduleType;
  scheduleNotes: string | null;
  preferredStartTime: string | null;
  preferredEndTime: string | null;
  posterPaymentAmountCents: number | null;
  volunteer: boolean;
  communityServiceHoursEligible?: boolean;
  communityServiceProofDetails?: string | null;
  estimatedDurationMinutes: number | null;
  teensRequired?: number | null;
};

type CreateGigModalProps = {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateGigRequest, images: File[], analyticsContext?: AdultGigCreateFlowAnalyticsContext) => Promise<void>;
  modalTitle?: string;
  publishLabel?: string;
  publishingLabel?: string;
  initialValues?: CreateGigFormValues | null;
  showSaveDraft?: boolean;
  draftLabel?: string;
  draftingLabel?: string;
  restrictPublishedFields?: boolean;
  analyticsSource?: AdultGigCreateFlowSource;
  analyticsEntryPoint?: AdultGigCreateFlowEntryPoint;
  analyticsMode?: AdultGigCreateFlowMode;
};

type DueDatePreset = PresetDueDateRange | "FLEXIBLE" | "CUSTOM";
type SimpleDueDatePreset = Exclude<DueDatePreset, "CUSTOM">;
type CustomScheduleDateMode = "SPECIFIC_DATE" | "DATE_RANGE";
type DurationPreset = "ONE_HOUR" | "TWO_HOURS" | "THREE_HOURS" | "FOUR_HOURS" | "FIVE_HOURS" | "CUSTOM";
type CreateGigFlowStep = "BASICS" | "PAYMENT" | "WORK" | "TIMING";
const estimatedMinuteOptions = ["0", "15", "30", "45"] as const;
const teenCountOptions = [1, 2, 3] as const;

type CreateGigCopy = {
  categoryLabel: string;
  categoryPlaceholder: string;
  categoryHelperText: string;
  titleLabel: string;
  titlePlaceholder: string;
  titleHelperText: string;
  locationLabel: string;
  locationTooltip: string;
  locationEditHint: string;
  paymentLabel: string;
  paymentPlaceholder: string;
  paymentTooltip: string;
  paymentEmptyHelperText: string;
  paymentStaticHelperText: string | null;
  paymentRequiredError: string;
  paymentMinimumError: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  descriptionHelperText: string;
  descriptionChecklistTitle: string | null;
  descriptionChecklistText: string | null;
  equipmentToggleLabel: string;
  equipmentDescriptionLabel: string;
  equipmentDescriptionPlaceholder: string;
  equipmentDescriptionRequiredError: string;
  photosLabel: string;
  photosHelperText: string | null;
  scheduleLabel: string;
  scheduleHelperText: string | null;
  flexibleTimingLabel: string;
  flexibleTimingTooltip: string;
  flexibleTimingPlaceholder: string;
  flexibleTimingRequiredError: string;
  customScheduleLabel: string;
  preferredTimeLabel: string;
  preferredTimeTooltip: string;
  scheduleSummaryCustomSpecificLead: string;
  scheduleSummaryCustomLead: string;
  scheduleSummaryPresetLead: string;
  durationLabel: string;
  locationRequiredError: string;
  volunteerPublishedMessage: string;
};

const getCreateGigCopy = (): CreateGigCopy => ({
    categoryLabel: "Category",
    categoryPlaceholder: "Yard Work",
    categoryHelperText: "Choose a common category or type your own.",
    titleLabel: "Title",
    titlePlaceholder: "Lawn mowing this weekend",
    titleHelperText: "Use a short, clear title for the work.",
    locationLabel: "Gig Location",
    locationTooltip:
      "Choose whether this gig is remote or in person. For in-person gigs, enter the ZIP code and we'll fill the default city.",
    locationEditHint: "Click to edit location",
    paymentLabel: "Pay per teen (USD)",
    paymentPlaceholder: "40.00",
    paymentTooltip:
      "Enter the amount you want to pay for each teen, not an hourly rate. You will only be charged after the gig has every requested spot selected and you continue through Stripe.",
    paymentEmptyHelperText:
      "Enter the amount you want to pay per teen. The estimated total updates from the number of teens needed.",
    paymentStaticHelperText:
      "MyTeenGig includes a platform fee to support secure payments, messaging, notifications, support, and safety features.",
    paymentRequiredError: "Please enter the amount you want to pay per teen.",
    paymentMinimumError: "Increase the per-teen amount so each teen payout is at least $1.00 after MyTeenGig fees.",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Describe the work and expectations.",
    descriptionHelperText: "Be specific about the work, expectations, and anything the teen should know before applying.",
    descriptionChecklistTitle: null,
    descriptionChecklistText: null,
    equipmentToggleLabel: "This gig requires tools, supplies, or equipment",
    equipmentDescriptionLabel: "What equipment is needed?",
    equipmentDescriptionPlaceholder: "Example: I will provide the mower and gloves. Teen should wear closed-toe shoes.",
    equipmentDescriptionRequiredError: "Describe the tools, supplies, or equipment needed for this gig.",
    photosLabel: "Add Photos",
    photosHelperText: null,
    scheduleLabel: "When does this need to be done?",
    scheduleHelperText: null,
    flexibleTimingLabel: "Timing Details",
    flexibleTimingTooltip:
      "Choose this when the exact date is open. Teens can apply with a proposed time, and you can coordinate before accepting someone.",
    flexibleTimingPlaceholder: "Examples: We can coordinate after booking, weekday afternoons usually work, open to different times.",
    flexibleTimingRequiredError: "Add timing details so teens know how to coordinate with you.",
    customScheduleLabel: "Custom scheduling",
    preferredTimeLabel: "What time window works best for this gig?",
    preferredTimeTooltip: "This is the time range you would prefer a teen to come complete the gig.",
    scheduleSummaryCustomSpecificLead: "Teens will see that this job should happen on",
    scheduleSummaryCustomLead: "Teens will see that this job can happen between",
    scheduleSummaryPresetLead: "Teens will see that this job needs to be done",
    durationLabel: "How long do you think this gig will take to complete?",
    locationRequiredError: "Enter a valid ZIP code for this in-person gig.",
    volunteerPublishedMessage: "This gig will be published as volunteer work with no payment amount shown.",
});

const toDateInputValue = (value: string | null, timeZone?: string | null) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return toZonedIsoDate(parsed, timeZone);
};

const hasSameLocalDate = (startValue: string | null, endValue: string | null, timeZone?: string | null) => {
  const startDate = toDateInputValue(startValue, timeZone);
  const endDate = toDateInputValue(endValue, timeZone);
  return startDate.length > 0 && startDate === endDate;
};

const sanitizePaymentInput = (value: string) => {
  if (value === "") {
    return "";
  }

  if (!/^\d*\.?\d*$/.test(value)) {
    return null;
  }

  const decimalIndex = value.indexOf(".");
  if (decimalIndex === -1) {
    return value;
  }

  const whole = value.slice(0, decimalIndex);
  const fraction = value.slice(decimalIndex + 1, decimalIndex + 3);
  return `${whole}.${fraction}`;
};

const dueDateOptions: ReadonlyArray<{ value: DueDatePreset; label: string; description: string }> = [
  {
    value: "NEXT_FEW_DAYS",
    label: "Next few days",
    description: "Good when you need help soon but the exact day can be flexible.",
  },
  {
    value: "THIS_WEEKEND",
    label: "This weekend",
    description: "Best for chores or projects that can happen Saturday or Sunday.",
  },
  {
    value: "THIS_WEEK",
    label: "This week",
    description: "Useful when it can be done anytime before the week wraps up.",
  },
  {
    value: "FLEXIBLE",
    label: "I'm flexible",
    description: "You can coordinate the timing after someone applies.",
  },
  {
    value: "CUSTOM",
    label: "Custom",
    description: "Pick a specific date or date range.",
  },
];

const customScheduleDateModeOptions: ReadonlyArray<{ value: CustomScheduleDateMode; label: string; description: string }> = [
  {
    value: "SPECIFIC_DATE",
    label: "Specific date",
    description: "Use when the gig needs to happen on one day.",
  },
  {
    value: "DATE_RANGE",
    label: "Date range",
    description: "Use when the gig can happen across multiple days.",
  },
];

const preferredTimeOptions: ReadonlyArray<{ value: PreferredTimePreset; label: string; description: string }> = [
  {
    value: "MORNING",
    label: "8am - 12pm",
    description: "Morning",
  },
  {
    value: "AFTERNOON",
    label: "12pm - 4pm",
    description: "Afternoon",
  },
  {
    value: "EVENING",
    label: "4pm - 8pm",
    description: "Evening",
  },
  {
    value: "CUSTOM",
    label: "Custom",
    description: "Add your own preferred time.",
  },
];

const durationOptions: ReadonlyArray<{
  value: DurationPreset;
  label: string;
  description: string;
  minutes: number | null;
}> = [
  {
    value: "ONE_HOUR",
    label: "1 hour",
    description: "",
    minutes: 60,
  },
  {
    value: "TWO_HOURS",
    label: "2 hours",
    description: "",
    minutes: 120,
  },
  {
    value: "THREE_HOURS",
    label: "3 hours",
    description: "",
    minutes: 180,
  },
  {
    value: "FOUR_HOURS",
    label: "4 hours",
    description: "",
    minutes: 240,
  },
  {
    value: "FIVE_HOURS",
    label: "5 hours",
    description: "",
    minutes: 300,
  },
  {
    value: "CUSTOM",
    label: "Custom",
    description: "Enter a custom duration.",
    minutes: null,
  },
];

const createGigFlowSteps: ReadonlyArray<{
  value: CreateGigFlowStep;
  label: string;
}> = [
  { value: "BASICS", label: "Basics" },
  { value: "PAYMENT", label: "Payment" },
  { value: "WORK", label: "Details" },
  { value: "TIMING", label: "Timing" },
];

const createGigAnalyticsFlowId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `gig-create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getCreateGigValidationErrorCode = (message: string) => {
  switch (message) {
    case "Choose a category for this gig.":
      return "category_required";
    case "Add a short, clear title for this gig.":
      return "title_required";
    case "Enter a valid ZIP code for this in-person gig.":
      return "location_required";
    case "Please enter the amount you want to pay per teen.":
      return "payment_required";
    case "The estimated total for this gig cannot exceed $10,000.":
      return "payment_total_cap";
    case "Increase the per-teen amount so each teen payout is at least $1.00 after MyTeenGig fees.":
      return "payment_minimum";
    case "Estimated time must use non-negative hours and minutes (0-59).":
      return "estimated_duration_invalid";
    case "Estimated time is required.":
      return "estimated_duration_required";
    case "Estimated time cannot exceed 24 hours.":
      return "estimated_duration_limit";
    case "Add a description for this gig.":
      return "description_required";
    case GIG_DESCRIPTION_CONTACT_INFO_BLOCK_MESSAGE:
      return "description_contact_info";
    case "Describe the tools, supplies, or equipment needed for this gig.":
      return "equipment_description_required";
    case "Add timing details so teens know how to coordinate with you.":
      return "flexible_timing_required";
    case "Choose a start and end time for the custom time window.":
      return "custom_time_window_required";
    case "Custom time window end must be after the start time.":
      return "custom_time_window_order";
    case "That preset time window has already passed. Pick another timing option or use Custom.":
      return "preset_window_past";
    case "Date is required.":
      return "custom_date_required";
    case "Start date is required.":
      return "custom_start_date_required";
    case "End date is required.":
      return "custom_end_date_required";
    case "End date must be after start date. Use Specific date for a one-day gig.":
      return "custom_date_range_order";
    default:
      return "unknown";
  }
};

const SIMPLE_WHEN_PREFIX = "Need this done:";
const SIMPLE_TIME_PREFIX = "Preferred time:";
const SIMPLE_FLEXIBLE_DETAILS_PREFIX = "Timing details:";

const buildPresetScheduleSignature = (
  dueDatePreset: SimpleDueDatePreset,
  preferredTimePreset: PreferredTimePreset,
  customPreferredTime: string,
  flexibleTimingDetails: string,
) => JSON.stringify({
  dueDatePreset,
  preferredTimePreset,
  customPreferredTime: customPreferredTime.trim(),
  flexibleTimingDetails: flexibleTimingDetails.trim(),
});

const isResolvedPresetDueDate = (value: DueDatePreset): value is PresetDueDateRange =>
  value === "NEXT_FEW_DAYS" || value === "THIS_WEEKEND" || value === "THIS_WEEK";

const getSelectionCardClassName = (selected: boolean, alignment: "left" | "center" = "left") =>
  [
    "rounded-2xl border px-4 py-4 transition",
    alignment === "center" ? "text-center" : "text-left",
    selected
      ? "border-myteengig-primary bg-myteengig-primary/10 shadow-sm"
      : "border-myteengig-border bg-white hover:border-myteengig-primary/50 hover:bg-myteengig-background/70",
  ].join(" ");

const getDueDateLabel = (value: SimpleDueDatePreset) =>
  dueDateOptions.find((option) => option.value === value)?.label ?? value;

const getPreferredTimeLabel = (value: PreferredTimePreset, customPreferredTime: string) => {
  if (value === "CUSTOM") {
    return customPreferredTime.trim();
  }

  return preferredTimeOptions.find((option) => option.value === value)?.label ?? value;
};

const PREFERRED_TIME_RANGE_BY_PRESET: Record<Exclude<PreferredTimePreset, "CUSTOM">, { start: string; end: string }> = {
  MORNING: { start: "08:00", end: "12:00" },
  AFTERNOON: { start: "12:00", end: "16:00" },
  EVENING: { start: "16:00", end: "20:00" },
};

const toTimeInputValue = (value: string | null) => {
  if (!value) {
    return "";
  }
  return value.slice(0, 5);
};

const getPreferredTimeValueRange = (
  preferredTimePreset: PreferredTimePreset,
  customPreferredStartTime: string,
  customPreferredEndTime: string,
) => {
  if (preferredTimePreset === "CUSTOM") {
    return {
      preferredStartTime: customPreferredStartTime || null,
      preferredEndTime: customPreferredEndTime || null,
    };
  }

  return {
    preferredStartTime: PREFERRED_TIME_RANGE_BY_PRESET[preferredTimePreset].start,
    preferredEndTime: PREFERRED_TIME_RANGE_BY_PRESET[preferredTimePreset].end,
  };
};

const getPreferredTimePresetFromRange = (
  preferredStartTime: string | null,
  preferredEndTime: string | null,
): PreferredTimePreset => {
  if (!preferredStartTime || !preferredEndTime) {
    return "AFTERNOON";
  }

  const matchingPreset = (Object.entries(PREFERRED_TIME_RANGE_BY_PRESET) as Array<
    [Exclude<PreferredTimePreset, "CUSTOM">, { start: string; end: string }]
  >).find(([, range]) => range.start === preferredStartTime && range.end === preferredEndTime);

  return matchingPreset?.[0] ?? "CUSTOM";
};

const formatCustomTimeInputLabel = (value: string) => {
  if (!value) {
    return "";
  }

  const [hoursPart, minutesPart] = value.split(":").map(Number);
  if (!Number.isInteger(hoursPart) || !Number.isInteger(minutesPart)) {
    return "";
  }

  const meridiem = hoursPart >= 12 ? "PM" : "AM";
  const normalizedHour = hoursPart % 12 || 12;
  return `${normalizedHour}:${String(minutesPart).padStart(2, "0")} ${meridiem}`;
};

const buildCustomPreferredTimeWindowLabel = (start: string, end: string) => {
  const formattedStart = formatCustomTimeInputLabel(start);
  const formattedEnd = formatCustomTimeInputLabel(end);
  if (!formattedStart || !formattedEnd) {
    return "";
  }
  return `${formattedStart} to ${formattedEnd}`;
};

const parseCustomPreferredTimeWindowLabel = (value: string) => {
  const match = value
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)\s+to\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) {
    return null;
  }

  const to24HourTime = (hoursText: string, minutesText: string, meridiem: string) => {
    const hours = Number(hoursText);
    const minutes = Number(minutesText);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
      return null;
    }

    const normalizedHours = meridiem.toUpperCase() === "PM"
      ? (hours % 12) + 12
      : hours % 12;
    return `${String(normalizedHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const start = to24HourTime(match[1], match[2], match[3]);
  const end = to24HourTime(match[4], match[5], match[6]);
  if (!start || !end) {
    return null;
  }

  return { start, end };
};

const buildSimpleScheduleNotes = (
  dueDatePreset: SimpleDueDatePreset,
  preferredTimePreset: PreferredTimePreset,
  customPreferredTime: string,
  flexibleTimingDetails: string,
) => {
  const lines = [`${SIMPLE_WHEN_PREFIX} ${getDueDateLabel(dueDatePreset)}`];

  if (dueDatePreset === "FLEXIBLE") {
    lines.push(`${SIMPLE_FLEXIBLE_DETAILS_PREFIX} ${flexibleTimingDetails.trim()}`);
    return lines.join("\n");
  }

  lines.push(`${SIMPLE_TIME_PREFIX} ${getPreferredTimeLabel(preferredTimePreset, customPreferredTime)}`);
  return lines.join("\n");
};

const parseSimpleScheduleNotes = (value: string | null) => {
  if (!value) {
    return null;
  }

  const lines = value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const whenLine = lines.find((line) => line.startsWith(SIMPLE_WHEN_PREFIX));
  const timeLine = lines.find((line) => line.startsWith(SIMPLE_TIME_PREFIX));

  if (!whenLine) {
    return null;
  }

  const whenLabel = whenLine.slice(SIMPLE_WHEN_PREFIX.length).trim();
  const dueDatePreset = dueDateOptions.find(
    (option) => option.value !== "CUSTOM" && option.label.toLowerCase() === whenLabel.toLowerCase(),
  );

  if (!dueDatePreset || dueDatePreset.value === "CUSTOM") {
    return null;
  }

  const flexibleDetailsLine = lines.find((line) => line.startsWith(SIMPLE_FLEXIBLE_DETAILS_PREFIX));
  if (dueDatePreset.value === "FLEXIBLE") {
    return {
      dueDatePreset: dueDatePreset.value,
      preferredTimePreset: "AFTERNOON" as const,
      customPreferredTime: "",
      flexibleTimingDetails: flexibleDetailsLine ? flexibleDetailsLine.slice(SIMPLE_FLEXIBLE_DETAILS_PREFIX.length).trim() : "",
    };
  }

  if (!timeLine) {
    return null;
  }

  const timeLabel = timeLine.slice(SIMPLE_TIME_PREFIX.length).trim();
  const preferredTimePreset = preferredTimeOptions.find(
    (option) =>
      option.value !== "CUSTOM"
      && (
        option.label.toLowerCase() === timeLabel.toLowerCase()
        || (option.value === "MORNING" && timeLabel.toLowerCase() === "8-12")
        || (option.value === "AFTERNOON" && timeLabel.toLowerCase() === "12-4")
        || (option.value === "EVENING" && timeLabel.toLowerCase() === "4-8")
      ),
  );

  return {
    dueDatePreset: dueDatePreset.value,
    preferredTimePreset: preferredTimePreset?.value ?? "CUSTOM",
    customPreferredTime: preferredTimePreset ? "" : timeLabel,
    flexibleTimingDetails: "",
  };
};

const getDurationPresetFromMinutes = (value: number | null): DurationPreset => {
  const matchingOption = durationOptions.find((option) => option.minutes === value);
  return matchingOption?.value ?? "CUSTOM";
};

const TimeSlotIcon = ({ preset }: { preset: PreferredTimePreset }) => {
  const commonProps = {
    className: "h-5 w-5 text-myteengig-primary",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    viewBox: "0 0 24 24",
  };

  if (preset === "MORNING") {
    return (
      <svg aria-hidden="true" {...commonProps}>
        <path d="M4 15h16" />
        <path d="M6 15a6 6 0 0 1 12 0" />
        <path d="M12 4v3" />
        <path d="m5.6 7.6 2.1 2.1" />
        <path d="m18.4 7.6-2.1 2.1" />
      </svg>
    );
  }

  if (preset === "AFTERNOON") {
    return (
      <svg aria-hidden="true" {...commonProps}>
        <circle cx="12" cy="12" r="4.5" />
        <path d="M12 2.5v2.5" />
        <path d="M12 19v2.5" />
        <path d="m4.9 4.9 1.8 1.8" />
        <path d="m17.3 17.3 1.8 1.8" />
        <path d="M2.5 12H5" />
        <path d="M19 12h2.5" />
        <path d="m4.9 19.1 1.8-1.8" />
        <path d="m17.3 6.7 1.8-1.8" />
      </svg>
    );
  }

  if (preset === "EVENING") {
    return (
      <svg aria-hidden="true" {...commonProps}>
        <path d="M4 16h16" />
        <path d="M7 16a5 5 0 0 1 10 0" />
        <path d="m8 7 1.2 1.2" />
        <path d="m16 7-1.2 1.2" />
        <path d="M12 5v2" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" {...commonProps}>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 8v4l2.5 2.5" />
    </svg>
  );
};

const CreateGigModal = ({
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
  modalTitle = "Create Gig",
  publishLabel = "Publish Gig",
  publishingLabel = "Publishing...",
  initialValues = null,
  showSaveDraft = true,
  draftLabel = "Save draft",
  draftingLabel = "Saving draft...",
  restrictPublishedFields = false,
  analyticsSource = "adult_posted_gigs",
  analyticsEntryPoint,
  analyticsMode = initialValues ? "draft" : "create",
}: CreateGigModalProps) => {
  const posthog = usePostHog();
  const { user } = useAuth();
  const canCreateVolunteerGig = hasRole(user?.roles, "VOLUNTEER");
  const [createError, setCreateError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [opportunityType, setOpportunityType] = useState<GigOpportunityType>("ONE_TIME");
  const [requiresEquipment, setRequiresEquipment] = useState(false);
  const [equipmentDescription, setEquipmentDescription] = useState("");
  const [category, setCategory] = useState("");
  const [activeFlowStep, setActiveFlowStep] = useState<CreateGigFlowStep>("BASICS");
  const isGuidedCreateFlow = !initialValues && !restrictPublishedFields;
  const copy = getCreateGigCopy();
  const [isEditingGigLocation, setIsEditingGigLocation] = useState(false);
  const [locationType, setLocationType] = useState<LocationType>("IN_PERSON");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [resolvedLocationTimeZone, setResolvedLocationTimeZone] = useState<string | null>(null);
  const [teensRequired, setTeensRequired] = useState(1);
  const [paymentAmountDollars, setPaymentAmountDollars] = useState("");
  const [isVolunteerGig, setIsVolunteerGig] = useState(false);
  const [communityServiceHoursEligible, setCommunityServiceHoursEligible] = useState(false);
  const [communityServiceProofDetails, setCommunityServiceProofDetails] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");
  const [estimatedMinutes, setEstimatedMinutes] = useState("");
  const [durationPreset, setDurationPreset] = useState<DurationPreset>("TWO_HOURS");
  const [dueDatePreset, setDueDatePreset] = useState<DueDatePreset>("NEXT_FEW_DAYS");
  const [preferredTimePreset, setPreferredTimePreset] = useState<PreferredTimePreset>("AFTERNOON");
  const [customPreferredTime, setCustomPreferredTime] = useState("");
  const [customPreferredStartTime, setCustomPreferredStartTime] = useState("");
  const [customPreferredEndTime, setCustomPreferredEndTime] = useState("");
  const [flexibleTimingDetails, setFlexibleTimingDetails] = useState("");
  const [customScheduleDateMode, setCustomScheduleDateMode] = useState<CustomScheduleDateMode>("SPECIFIC_DATE");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [minimumStartDate, setMinimumStartDate] = useState("");
  const [submitMode, setSubmitMode] = useState<EditableGigStatus>("PUBLISHED");
  const hasAttemptedLocationAutofillRef = useRef(false);
  const initialPresetScheduleSignatureRef = useRef<string | null>(null);
  const activeAnalyticsContextRef = useRef<AdultGigCreateFlowAnalyticsContext | null>(null);
  const analyticsOpenedAtRef = useRef<number | null>(null);
  const trackedAnalyticsStepsRef = useRef<Set<CreateGigFlowStep>>(new Set());
  const isRecurringIntro = opportunityType === "RECURRING_INTRO";
  const formattedDescription = formatGigOpportunityDescription({
    type: opportunityType,
    description,
  });
  const descriptionContainsBlockedContactInfo = containsBlockedContactInfo(formattedDescription);
  const browserTimeZone = getBrowserTimeZone();
  const remoteTimeZone = initialValues?.locationType === "REMOTE"
    ? initialValues.timeZone ?? browserTimeZone
    : browserTimeZone;
  const requestedPayloadTimeZone = locationType === "REMOTE"
    ? remoteTimeZone
    : resolvedLocationTimeZone ?? initialValues?.timeZone ?? browserTimeZone;
  const effectiveScheduleTimeZone = resolveGigTimeZone(requestedPayloadTimeZone);

  const getAnalyticsStepProperties = useCallback((step: CreateGigFlowStep = activeFlowStep) => {
    const context = activeAnalyticsContextRef.current;
    if (!context) {
      return null;
    }

    const stepIndex = Math.max(0, createGigFlowSteps.findIndex((flowStep) => flowStep.value === step));
    return {
      ...context,
      step,
      step_index: stepIndex + 1,
      step_count: createGigFlowSteps.length,
    };
  }, [activeFlowStep]);

  const getAnalyticsElapsedSeconds = useCallback((openedAtMs = analyticsOpenedAtRef.current) => {
    if (openedAtMs === null) {
      return null;
    }

    return Math.max(0, Math.round((Date.now() - openedAtMs) / 1000));
  }, []);

  const setCreateValidationError = (message: string, action: AdultGigCreateFlowAction) => {
    setCreateError(message);
    const stepProperties = getAnalyticsStepProperties();
    if (!stepProperties) {
      return;
    }

    captureAdultGigCreateValidationFailed(posthog, {
      ...stepProperties,
      action,
      error_code: getCreateGigValidationErrorCode(message),
    });
  };

  const captureModalClosed = (reason: "cancelled" | "submitted", completed: boolean) => {
    const stepProperties = getAnalyticsStepProperties();
    if (!stepProperties) {
      return;
    }

    captureAdultGigCreateModalClosed(posthog, {
      ...stepProperties,
      reason,
      completed,
      elapsed_seconds: getAnalyticsElapsedSeconds(),
    });
  };

  const handleZipDetailsChange = useCallback((details: UsZipLocationResponseDto | null) => {
    setResolvedLocationTimeZone(details?.timeZone ?? null);
  }, []);

  const applyEstimatedDuration = (minutes: number) => {
    setEstimatedHours(String(Math.floor(minutes / 60)));
    setEstimatedMinutes(String(minutes % 60));
  };

  const resetCreateFormDefaults = useCallback(() => {
    setCreateError(null);
    setTitle("");
    setDescription("");
    setOpportunityType("ONE_TIME");
    setRequiresEquipment(false);
    setEquipmentDescription("");
    setCategory("");
    setActiveFlowStep("BASICS");
    setIsEditingGigLocation(false);
    setLocationType("IN_PERSON");
    setCity("");
    setState("");
    setZip("");
    setResolvedLocationTimeZone(null);
    setTeensRequired(1);
    setPaymentAmountDollars("");
    setIsVolunteerGig(false);
    setCommunityServiceHoursEligible(false);
    setCommunityServiceProofDetails("");
    setDurationPreset("TWO_HOURS");
    applyEstimatedDuration(120);
    setDueDatePreset("NEXT_FEW_DAYS");
    setPreferredTimePreset("AFTERNOON");
    setCustomPreferredTime("");
    setCustomPreferredStartTime("");
    setCustomPreferredEndTime("");
    setFlexibleTimingDetails("");
    setCustomScheduleDateMode("SPECIFIC_DATE");
    setStartTime("");
    setEndTime("");
    setImages([]);
    setSubmitMode("PUBLISHED");
    initialPresetScheduleSignatureRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setMinimumStartDate(toZonedIsoDate(toMinutePrecision(new Date()), effectiveScheduleTimeZone));
  }, [effectiveScheduleTimeZone, isOpen]);

  useEffect(() => {
    if (!isOpen || initialValues) {
      return;
    }

    resetCreateFormDefaults();
    setSubmitMode("PUBLISHED");
  }, [initialValues, isOpen, resetCreateFormDefaults]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!initialValues) {
      return;
    }

    setCreateError(null);
    setActiveFlowStep("BASICS");
    setTitle(initialValues.title ?? "");
    const opportunityDetails = parseGigOpportunityDescription(initialValues.description ?? "");
    setDescription(opportunityDetails.description);
    setOpportunityType(opportunityDetails.type);
    setRequiresEquipment(Boolean(initialValues.requiresEquipment));
    setEquipmentDescription(initialValues.equipmentDescription ?? "");
    setCategory(initialValues.category ?? "");
    setLocationType(initialValues.locationType ?? "IN_PERSON");
    setCity(initialValues.city ?? "");
    setState((initialValues.state ?? "").toUpperCase());
    setZip(initialValues.zip ?? "");
    setIsEditingGigLocation(false);
    setTeensRequired(normalizeTeensRequired(initialValues.teensRequired));
    setIsVolunteerGig(Boolean(initialValues.volunteer));
    setCommunityServiceHoursEligible(Boolean(initialValues.communityServiceHoursEligible));
    setCommunityServiceProofDetails(initialValues.communityServiceProofDetails ?? "");
    setPaymentAmountDollars(
      typeof initialValues.posterPaymentAmountCents === "number" ? (initialValues.posterPaymentAmountCents / 100).toFixed(2) : "",
    );
    const estimatedMinutesTotal = initialValues.estimatedDurationMinutes ?? 0;
    const nextDurationPreset = getDurationPresetFromMinutes(estimatedMinutesTotal);
    setDurationPreset(nextDurationPreset);
    setEstimatedHours(estimatedMinutesTotal > 0 ? String(Math.floor(estimatedMinutesTotal / 60)) : "");
    setEstimatedMinutes(estimatedMinutesTotal > 0 ? String(estimatedMinutesTotal % 60) : "");
    setResolvedLocationTimeZone(initialValues.locationType === "IN_PERSON" ? initialValues.timeZone ?? null : null);
    setStartTime(toDateInputValue(initialValues.startTime, initialValues.timeZone));
    setEndTime(toDateInputValue(initialValues.endTime, initialValues.timeZone));
    setCustomScheduleDateMode(
      initialValues.startTime && initialValues.endTime && !hasSameLocalDate(initialValues.startTime, initialValues.endTime, initialValues.timeZone)
        ? "DATE_RANGE"
        : "SPECIFIC_DATE",
    );
    setCustomPreferredStartTime(toTimeInputValue(initialValues.preferredStartTime));
    setCustomPreferredEndTime(toTimeInputValue(initialValues.preferredEndTime));
    const parsedSimpleSchedule = parseSimpleScheduleNotes(initialValues.scheduleNotes ?? null);
    if (parsedSimpleSchedule) {
      setDueDatePreset(parsedSimpleSchedule.dueDatePreset);
      setPreferredTimePreset(parsedSimpleSchedule.preferredTimePreset);
      setCustomPreferredTime(parsedSimpleSchedule.customPreferredTime);
      const parsedCustomWindow = initialValues.preferredStartTime && initialValues.preferredEndTime
        ? {
            start: toTimeInputValue(initialValues.preferredStartTime),
            end: toTimeInputValue(initialValues.preferredEndTime),
          }
        : parseCustomPreferredTimeWindowLabel(parsedSimpleSchedule.customPreferredTime);
      setCustomPreferredStartTime(parsedCustomWindow?.start ?? toTimeInputValue(initialValues.preferredStartTime));
      setCustomPreferredEndTime(parsedCustomWindow?.end ?? toTimeInputValue(initialValues.preferredEndTime));
      setFlexibleTimingDetails(parsedSimpleSchedule.flexibleTimingDetails);
    } else if (initialValues.scheduleType === "FLEXIBLE") {
      setDueDatePreset("FLEXIBLE");
      setPreferredTimePreset("AFTERNOON");
      setCustomPreferredTime("");
      setCustomPreferredStartTime("");
      setCustomPreferredEndTime("");
      setFlexibleTimingDetails(initialValues.scheduleNotes ?? "");
    } else {
      setDueDatePreset("CUSTOM");
      const inferredPreferredTimePreset = getPreferredTimePresetFromRange(
        initialValues.preferredStartTime,
        initialValues.preferredEndTime,
      );
      setPreferredTimePreset(inferredPreferredTimePreset);
      setCustomPreferredTime(
        inferredPreferredTimePreset === "CUSTOM"
          ? buildCustomPreferredTimeWindowLabel(
              toTimeInputValue(initialValues.preferredStartTime),
              toTimeInputValue(initialValues.preferredEndTime),
            )
          : "",
      );
      setCustomPreferredStartTime(
        inferredPreferredTimePreset === "CUSTOM" ? toTimeInputValue(initialValues.preferredStartTime) : "",
      );
      setCustomPreferredEndTime(
        inferredPreferredTimePreset === "CUSTOM" ? toTimeInputValue(initialValues.preferredEndTime) : "",
      );
      setFlexibleTimingDetails("");
    }
    initialPresetScheduleSignatureRef.current = parsedSimpleSchedule
      ? buildPresetScheduleSignature(
          parsedSimpleSchedule.dueDatePreset,
          parsedSimpleSchedule.preferredTimePreset,
          parsedSimpleSchedule.customPreferredTime,
          parsedSimpleSchedule.flexibleTimingDetails,
        )
      : null;
    setImages([]);
  }, [isOpen, initialValues]);

  useEffect(() => {
    if (!isOpen) {
      activeAnalyticsContextRef.current = null;
      analyticsOpenedAtRef.current = null;
      trackedAnalyticsStepsRef.current = new Set();
      return;
    }

    if (activeAnalyticsContextRef.current) {
      return;
    }

    const context: AdultGigCreateFlowAnalyticsContext = {
      flow_id: createGigAnalyticsFlowId(),
      source: analyticsSource,
      ...(analyticsEntryPoint ? { entry_point: analyticsEntryPoint } : {}),
      mode: analyticsMode,
      guided_flow: isGuidedCreateFlow,
    };

    activeAnalyticsContextRef.current = context;
    analyticsOpenedAtRef.current = Date.now();
    trackedAnalyticsStepsRef.current = new Set();

    captureAdultGigCreateModalOpened(posthog, {
      ...context,
      has_initial_values: Boolean(initialValues),
      restrict_published_fields: restrictPublishedFields,
    });
  }, [
    analyticsEntryPoint,
    analyticsMode,
    analyticsSource,
    initialValues,
    isGuidedCreateFlow,
    isOpen,
    posthog,
    restrictPublishedFields,
  ]);

  useEffect(() => {
    if (!isOpen || !isGuidedCreateFlow) {
      return;
    }

    const stepProperties = getAnalyticsStepProperties();
    if (!stepProperties || trackedAnalyticsStepsRef.current.has(activeFlowStep)) {
      return;
    }

    trackedAnalyticsStepsRef.current.add(activeFlowStep);
    captureAdultGigCreateStepViewed(posthog, stepProperties);
  }, [activeFlowStep, getAnalyticsStepProperties, isGuidedCreateFlow, isOpen, posthog]);

  useEffect(() => {
    if (!isOpen) {
      hasAttemptedLocationAutofillRef.current = false;
      return;
    }
    if (initialValues) {
      hasAttemptedLocationAutofillRef.current = true;
      return;
    }
    if (locationType === "REMOTE") {
      hasAttemptedLocationAutofillRef.current = true;
      return;
    }
    if (hasAttemptedLocationAutofillRef.current) {
      return;
    }

    const addressAlreadyEntered = city.trim().length > 0 || state.trim().length > 0 || zip.trim().length > 0;
    if (addressAlreadyEntered) {
      hasAttemptedLocationAutofillRef.current = true;
      return;
    }
    hasAttemptedLocationAutofillRef.current = true;

    let cancelled = false;
    const autofillFromLatestPostedGig = async () => {
      try {
        const postedGigs = await gigsApi.listAdultPostedGigs();
        const latestGig = postedGigs[0];
        if (!latestGig) {
          return;
        }

        const latestGigDetail = await gigsApi.getById(latestGig.id);
        if (cancelled) {
          return;
        }

        setLocationType(latestGigDetail.locationType ?? "IN_PERSON");
        setCity(latestGigDetail.locationType === "REMOTE" ? "" : latestGigDetail.city ?? "");
        setState(latestGigDetail.locationType === "REMOTE" ? "" : (latestGigDetail.state ?? "").toUpperCase());
        setZip(latestGigDetail.locationType === "REMOTE" ? "" : latestGigDetail.zip ?? "");
        setIsEditingGigLocation(false);
      } catch {
        // Autofill is best-effort. Keep form editable with manual entry.
      }
    };

    void autofillFromLatestPostedGig();
    return () => {
      cancelled = true;
    };
  }, [isOpen, initialValues, city, state, zip, locationType]);

  const handleClose = () => {
    captureModalClosed("cancelled", false);
    setCreateError(null);
    setSubmitMode("PUBLISHED");
    resetCreateFormDefaults();
    onClose();
  };

  const handleLocationTypeChange = (nextLocationType: LocationType) => {
    setLocationType(nextLocationType);
    setIsEditingGigLocation(true);
    if (nextLocationType === "REMOTE") {
      setCity("");
      setState("");
      setZip("");
      setResolvedLocationTimeZone(null);
      return;
    }
    if (!city.trim() && !state.trim() && !zip.trim()) {
      hasAttemptedLocationAutofillRef.current = false;
    }
  };

  const paymentRequiredError = copy.paymentRequiredError;
  const paymentMinimumError = copy.paymentMinimumError;

  const getPaymentValidationError = (posterPaymentPerTeenCents: number | null) => {
    if (isVolunteerGig) {
      return null;
    }
    if (posterPaymentPerTeenCents === null) {
      return paymentRequiredError;
    }
    if (posterPaymentPerTeenCents * teensRequired > 1_000_000) {
      return "The estimated total for this gig cannot exceed $10,000.";
    }
    const teenPayoutCents = calculateMultiTeenGigPricingFromPosterAmountCents(
      posterPaymentPerTeenCents,
      teensRequired,
    ).teenPayoutPerSeatCents;
    if (teenPayoutCents < 100) {
      return paymentMinimumError;
    }
    return null;
  };

  const getEstimatedDurationMinutesTotal = () => {
    const estimatedHoursNumber = estimatedHours.trim() === "" ? 0 : Number(estimatedHours);
    const estimatedMinutesNumber = estimatedMinutes.trim() === "" ? 0 : Number(estimatedMinutes);
    if (
      !Number.isInteger(estimatedHoursNumber) ||
      !Number.isInteger(estimatedMinutesNumber) ||
      estimatedHoursNumber < 0 ||
      estimatedMinutesNumber < 0 ||
      estimatedMinutesNumber > 59
    ) {
      return null;
    }
    return estimatedHoursNumber * 60 + estimatedMinutesNumber;
  };

  const getEstimatedDurationValidationError = () => {
    const estimatedDurationMinutesTotal = getEstimatedDurationMinutesTotal();
    if (estimatedDurationMinutesTotal === null) {
      return "Estimated time must use non-negative hours and minutes (0-59).";
    }
    if (estimatedDurationMinutesTotal <= 0) {
      return "Estimated time is required.";
    }
    if (estimatedDurationMinutesTotal > 1_440) {
      return "Estimated time cannot exceed 24 hours.";
    }
    return null;
  };

  const validateBasicsStep = () => {
    if (!category.trim()) {
      return "Choose a category for this gig.";
    }
    if (!title.trim()) {
      return "Add a short, clear title for this gig.";
    }
    if (locationType === "IN_PERSON" && (!city.trim() || state.trim().length !== 2 || !zip.trim())) {
      return copy.locationRequiredError;
    }
    return null;
  };

  const validatePaymentStep = () => {
    if (isVolunteerGig && communityServiceHoursEligible && !communityServiceProofDetails.trim()) {
      return "Describe the form or documentation you will provide to verify community service hours.";
    }
    const paymentError = getPaymentValidationError(parseUsdInputToCents(paymentAmountDollars));
    if (paymentError) {
      return paymentError;
    }
    return getEstimatedDurationValidationError();
  };

  const validateWorkStep = () => {
    if (!description.trim()) {
      return isRecurringIntro ? "Describe what the teen will do during the introductory gig." : "Add a description for this gig.";
    }
    if (descriptionContainsBlockedContactInfo) {
      return GIG_DESCRIPTION_CONTACT_INFO_BLOCK_MESSAGE;
    }
    if (requiresEquipment && equipmentDescription.trim().length === 0) {
      return copy.equipmentDescriptionRequiredError;
    }
    return null;
  };

  const getCurrentFlowStepError = () => {
    if (!isGuidedCreateFlow) {
      return null;
    }
    if (activeFlowStep === "BASICS") {
      return validateBasicsStep();
    }
    if (activeFlowStep === "PAYMENT") {
      return validatePaymentStep();
    }
    if (activeFlowStep === "WORK") {
      return validateWorkStep();
    }
    return null;
  };

  const handleNextFlowStep = () => {
    const stepError = getCurrentFlowStepError();
    if (stepError) {
      setCreateValidationError(stepError, "continue");
      return;
    }

    const currentStepIndex = createGigFlowSteps.findIndex((step) => step.value === activeFlowStep);
    const nextStep = createGigFlowSteps[currentStepIndex + 1]?.value;
    if (!nextStep) {
      return;
    }
    setCreateError(null);
    const stepProperties = getAnalyticsStepProperties();
    if (stepProperties) {
      captureAdultGigCreateStepCompleted(posthog, {
        ...stepProperties,
        next_step: nextStep,
      });
    }
    setActiveFlowStep(nextStep);
  };

  const handlePreviousFlowStep = () => {
    const currentStepIndex = createGigFlowSteps.findIndex((step) => step.value === activeFlowStep);
    const previousStep = createGigFlowSteps[currentStepIndex - 1]?.value;
    if (!previousStep) {
      return;
    }
    setCreateError(null);
    setActiveFlowStep(previousStep);
  };

  const handleCreateGig = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateError(null);

    if (isGuidedCreateFlow && activeFlowStep !== "TIMING") {
      handleNextFlowStep();
      return;
    }

    const workError = validateWorkStep();
    if (workError) {
      setCreateValidationError(workError, "submit");
      return;
    }

    if (descriptionContainsBlockedContactInfo) {
      setCreateValidationError(GIG_DESCRIPTION_CONTACT_INFO_BLOCK_MESSAGE, "submit");
      return;
    }

    const posterPaymentAmountCents = parseUsdInputToCents(paymentAmountDollars);
    const paymentValidationError = getPaymentValidationError(posterPaymentAmountCents);
    if (paymentValidationError) {
      setCreateValidationError(paymentValidationError, "submit");
      return;
    }

    const isCustomSchedule = dueDatePreset === "CUSTOM";
    let resolvedScheduleType: GigScheduleType = "FLEXIBLE";
    let resolvedScheduleNotes: string | null = null;
    let startTimeIso: string | null = null;
    let endTimeIso: string | null = null;
    let preferredStartTimeValue: string | null = null;
    let preferredEndTimeValue: string | null = null;

    if (!isCustomSchedule) {
      if (dueDatePreset === "FLEXIBLE") {
        resolvedScheduleType = "FLEXIBLE";
        if (flexibleTimingDetails.trim().length === 0) {
          setCreateValidationError(copy.flexibleTimingRequiredError, "submit");
          return;
        }

        resolvedScheduleNotes = buildSimpleScheduleNotes(
          dueDatePreset,
          preferredTimePreset,
          customPreferredTime,
          flexibleTimingDetails,
        );
      } else {
        resolvedScheduleType = "FIXED";
        const preferredTimeRange = getPreferredTimeValueRange(
          preferredTimePreset,
          customPreferredStartTime,
          customPreferredEndTime,
        );
        preferredStartTimeValue = preferredTimeRange.preferredStartTime;
        preferredEndTimeValue = preferredTimeRange.preferredEndTime;
        const resolvedCustomPreferredTime = preferredTimePreset === "CUSTOM"
          ? buildCustomPreferredTimeWindowLabel(customPreferredStartTime, customPreferredEndTime)
          : customPreferredTime;
        if (preferredTimePreset === "CUSTOM") {
          if (!customPreferredStartTime || !customPreferredEndTime) {
            setCreateValidationError("Choose a start and end time for the custom time window.", "submit");
            return;
          }
          if (customPreferredEndTime <= customPreferredStartTime) {
            setCreateValidationError("Custom time window end must be after the start time.", "submit");
            return;
          }
        }

        resolvedScheduleNotes = buildSimpleScheduleNotes(
          dueDatePreset,
          preferredTimePreset,
          resolvedCustomPreferredTime,
          flexibleTimingDetails,
        );

        const currentPresetSignature = buildPresetScheduleSignature(
          dueDatePreset,
          preferredTimePreset,
          resolvedCustomPreferredTime,
          flexibleTimingDetails,
        );
        const canReuseInitialPresetWindow =
          initialValues?.scheduleType === "FIXED"
          && initialValues.startTime !== null
          && initialValues.endTime !== null
          && initialPresetScheduleSignatureRef.current !== null
          && currentPresetSignature === initialPresetScheduleSignatureRef.current;

        if (canReuseInitialPresetWindow) {
          startTimeIso = initialValues.startTime;
          endTimeIso = initialValues.endTime;
        } else if (isResolvedPresetDueDate(dueDatePreset)) {
          const resolvedWindow = resolvePresetScheduleWindow(dueDatePreset, preferredTimePreset, new Date(), effectiveScheduleTimeZone);
          if (!resolvedWindow) {
            setCreateValidationError("That preset time window has already passed. Pick another timing option or use Custom.", "submit");
            return;
          }
          startTimeIso = resolvedWindow.startTime.toISOString();
          endTimeIso = resolvedWindow.endTime.toISOString();
        }
      }
    } else {
      resolvedScheduleType = "FIXED";
      const preferredTimeRange = getPreferredTimeValueRange(
        preferredTimePreset,
        customPreferredStartTime,
        customPreferredEndTime,
      );
      preferredStartTimeValue = preferredTimeRange.preferredStartTime;
      preferredEndTimeValue = preferredTimeRange.preferredEndTime;

      const startDate = zonedStartOfDay(startTime, effectiveScheduleTimeZone);
      const endDate = customScheduleDateMode === "SPECIFIC_DATE"
        ? zonedEndOfDay(startTime, effectiveScheduleTimeZone)
        : zonedEndOfDay(endTime, effectiveScheduleTimeZone);

      if (!startTime || !startDate) {
        setCreateValidationError(customScheduleDateMode === "SPECIFIC_DATE" ? "Date is required." : "Start date is required.", "submit");
        return;
      }
      if (customScheduleDateMode === "DATE_RANGE" && (!endTime || !endDate)) {
        setCreateValidationError("End date is required.", "submit");
        return;
      }
      if (!endDate) {
        setCreateValidationError("End date is required.", "submit");
        return;
      }
      if (preferredTimePreset === "CUSTOM") {
        if (!customPreferredStartTime || !customPreferredEndTime) {
          setCreateValidationError("Choose a start and end time for the custom time window.", "submit");
          return;
        }
        if (customPreferredEndTime <= customPreferredStartTime) {
          setCreateValidationError("Custom time window end must be after the start time.", "submit");
          return;
        }
      }
      startTimeIso = toIsoTimestamp(startDate);
      endTimeIso = toIsoTimestamp(endDate);

      if (customScheduleDateMode === "DATE_RANGE" && endTime <= startTime) {
        setCreateValidationError("End date must be after start date. Use Specific date for a one-day gig.", "submit");
        return;
      }
    }

    const estimatedDurationValidationError = getEstimatedDurationValidationError();
    if (estimatedDurationValidationError) {
      setCreateValidationError(estimatedDurationValidationError, "submit");
      return;
    }
    const estimatedDurationMinutesTotal = getEstimatedDurationMinutesTotal() ?? 0;

    if (locationType === "IN_PERSON" && (!city.trim() || state.trim().length !== 2 || !zip.trim())) {
      setCreateValidationError(copy.locationRequiredError, "submit");
      return;
    }
    const trimmedEquipmentDescription = equipmentDescription.trim();
    if (isVolunteerGig && communityServiceHoursEligible && !communityServiceProofDetails.trim()) {
      setCreateValidationError("Describe the form or documentation you will provide to verify community service hours.", "submit");
      return;
    }
    if (requiresEquipment && trimmedEquipmentDescription.length === 0) {
      setCreateValidationError(copy.equipmentDescriptionRequiredError, "submit");
      return;
    }
    const payload: CreateGigRequest = {
      title: title.trim(),
      description: formattedDescription,
      requiresEquipment,
      equipmentDescription: requiresEquipment ? trimmedEquipmentDescription : null,
      category: category.trim(),
      locationType,
      city: locationType === "IN_PERSON" ? city.trim() : null,
      state: locationType === "IN_PERSON" ? state.trim().toUpperCase() : null,
      zip: locationType === "IN_PERSON" ? zip.trim() : null,
      timeZone: effectiveScheduleTimeZone,
      startTime: startTimeIso,
      endTime: endTimeIso,
      scheduleType: resolvedScheduleType,
      scheduleNotes: resolvedScheduleNotes,
      preferredStartTime: preferredStartTimeValue,
      preferredEndTime: preferredEndTimeValue,
      paymentAmountCents: isVolunteerGig ? null : posterPaymentAmountCents,
      volunteer: isVolunteerGig,
      communityServiceHoursEligible: isVolunteerGig && communityServiceHoursEligible,
      communityServiceProofDetails: isVolunteerGig && communityServiceHoursEligible ? communityServiceProofDetails.trim() : null,
      estimatedDurationMinutes: estimatedDurationMinutesTotal,
      status: submitMode,
      teensRequired,
    };

    try {
      const analyticsContext = activeAnalyticsContextRef.current ?? undefined;
      const submittedCloseStepProperties = getAnalyticsStepProperties();
      const submittedOpenedAtMs = analyticsOpenedAtRef.current;
      if (analyticsContext) {
        captureAdultGigCreateSubmitted(posthog, {
          ...analyticsContext,
          status: payload.status === "DRAFT" ? "DRAFT" : "PUBLISHED",
          category: payload.category,
          volunteer: Boolean(payload.volunteer),
          schedule_type: payload.scheduleType ?? "FIXED",
          location_type: payload.locationType ?? locationType,
          has_images: images.length > 0,
          image_count: images.length,
          teens_required: payload.teensRequired ?? teensRequired,
          poster_payment_amount_cents: payload.paymentAmountCents,
          estimated_duration_minutes: payload.estimatedDurationMinutes,
        });
      }
      await onSubmit(payload, images, analyticsContext);
      if (submittedCloseStepProperties) {
        captureAdultGigCreateModalClosed(posthog, {
          ...submittedCloseStepProperties,
          reason: "submitted",
          completed: true,
          elapsed_seconds: getAnalyticsElapsedSeconds(submittedOpenedAtMs),
        });
      }
      onClose();
      resetCreateFormDefaults();
    } catch (createGigError) {
      setCreateError(apiErrorToMessage(createGigError, "Failed to create gig."));
    }
  };

  const onImageFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selectedFiles.length === 0) {
      return;
    }

    setImages((current) => {
      const next = [...current];
      for (const file of selectedFiles) {
        if (next.length >= 5) {
          break;
        }
        next.push(file);
      }
      return next;
    });

    if (images.length + selectedFiles.length > 5) {
      setCreateError("A gig can only have up to 5 photos.");
    }
  };

  const removeImage = (index: number) => {
    setImages((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const hasResolvedGigLocation = locationType === "REMOTE" || (city.trim().length > 0 && state.trim().length === 2 && zip.trim().length > 0);
  const showGigLocationEditor = locationType === "REMOTE" || !hasResolvedGigLocation || isEditingGigLocation;
  const gigLocationSummary = locationType === "REMOTE"
    ? "Remote"
    : hasResolvedGigLocation
      ? `${city.trim()}, ${state.trim().toUpperCase()}`
      : "Add location";
  const fullGigLocation = locationType === "REMOTE"
    ? "Remote gig"
    : hasResolvedGigLocation
    ? [city.trim(), state.trim().toUpperCase(), zip.trim()].filter(Boolean).join(" ")
    : "Add a valid ZIP code for this gig.";
  const parsedPosterPaymentPerTeenCents = !isVolunteerGig ? parseUsdInputToCents(paymentAmountDollars) : null;
  const paymentPricing = parsedPosterPaymentPerTeenCents !== null
    ? calculateMultiTeenGigPricingFromPosterAmountCents(parsedPosterPaymentPerTeenCents, teensRequired)
    : null;
  const estimatedTotalDueCents = paymentPricing?.totalPosterPaymentAmountCents ?? 0;
  const paymentLabel = copy.paymentLabel;
  const paymentTooltip = copy.paymentTooltip;
  const paymentEmptyHelperText = copy.paymentEmptyHelperText;
  const descriptionHelperText = isRecurringIntro
    ? "Describe the first gig's tasks and expectations. If you hope for ongoing help, include the kind of work and how often you may need it. Payment here covers only the first gig."
    : isGuidedCreateFlow
      ? "Describe the gig, expectations, and anything the teen should know before applying."
      : copy.descriptionHelperText;
  const descriptionPlaceholder = isRecurringIntro
    ? "For example: Start with a two-hour babysitting gig to meet our family. If it goes well, we may need help twice a week after school."
    : copy.descriptionPlaceholder;
  const scheduleLabel = isRecurringIntro
    ? "When would you like the introductory gig to happen?"
    : isGuidedCreateFlow ? "When should this gig happen?" : copy.scheduleLabel;
  const durationLabel = isRecurringIntro
    ? "How long would you like the introductory gig to be?"
    : copy.durationLabel;
	  const paymentHelperText = (() => {
	    if (isVolunteerGig) {
	      return "";
	    }
    if (!paymentAmountDollars.trim()) {
      return paymentEmptyHelperText;
    }
    if (parsedPosterPaymentPerTeenCents !== null && parsedPosterPaymentPerTeenCents * teensRequired > 1_000_000) {
      return "The estimated total for this gig cannot exceed $10,000.";
    }
    if (paymentPricing === null || paymentPricing.teenPayoutPerSeatCents < 100) {
      return paymentMinimumError;
    }
    return (
      <>
        <span className="font-semibold text-myteengig-text-primary">
          Each teen will be paid approximately {formatUsdFromCents(paymentPricing.teenPayoutPerSeatCents)}
        </span>{" "}
	        after MyTeenGig fees.
	      </>
	    );
	  })();
  const estimatedDurationMinutesForPreview = getEstimatedDurationMinutesTotal();
  const teenHourlyEquivalentCents =
	    !isVolunteerGig
	    && paymentPricing !== null
    && parsedPosterPaymentPerTeenCents !== null
    && parsedPosterPaymentPerTeenCents * teensRequired <= 1_000_000
	    && paymentPricing.teenPayoutPerSeatCents >= 100
    && estimatedDurationMinutesForPreview !== null
    && estimatedDurationMinutesForPreview > 0
      ? Math.round((paymentPricing.teenPayoutPerSeatCents * 60) / estimatedDurationMinutesForPreview)
      : null;
	  const customMinuteOptions = estimatedMinuteOptions.includes(estimatedMinutes as typeof estimatedMinuteOptions[number])
	    ? estimatedMinuteOptions
	    : [...estimatedMinuteOptions, estimatedMinutes].filter((value, index, values) => value !== "" && values.indexOf(value) === index);
  const lockedFieldClassName = restrictPublishedFields ? "cursor-not-allowed bg-slate-100 text-slate-500" : "bg-white";
  const disableLockedPublishedFields = restrictPublishedFields;
  const teensRequiredField = (
    <div>
      <p id="gig-teens-required-label" className="mb-2 text-sm font-medium text-myteengig-text-primary">
        Number of teens needed
      </p>
      <div
        role="group"
        aria-labelledby="gig-teens-required-label"
        className="grid grid-cols-3 overflow-hidden rounded-lg border border-myteengig-border bg-white p-1"
      >
        {teenCountOptions.map((option) => {
          const isSelected = teensRequired === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={isSelected}
              disabled={disableLockedPublishedFields}
              onClick={() => setTeensRequired(option)}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-myteengig-primary/30 ${
                isSelected
                  ? "bg-myteengig-primary text-white shadow-sm"
                  : "text-myteengig-text-muted hover:bg-myteengig-background hover:text-myteengig-text-primary"
              } ${disableLockedPublishedFields ? "cursor-not-allowed opacity-70" : ""}`}
            >
              {option === 1 ? "1 teen" : `${option} teens`}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-myteengig-text-muted">
        You will choose applicants later. Payment starts only after all requested spots are selected.
      </p>
    </div>
  );
		  const paymentField = !isVolunteerGig ? (
	    <div>
	      <div className="mb-1 flex items-center justify-center gap-2 text-center">
	        <label htmlFor="gig-payment" className="text-sm font-medium text-myteengig-text-primary">
	          {paymentLabel}
	        </label>
		        <InfoTooltip
		          panelPositionClassName="sm:top-full sm:right-0 sm:mt-2"
		          label="How payment works"
		          message={paymentTooltip}
		        />
	      </div>
      <input
        id="gig-payment"
        type="number"
        required
        min="0.01"
        max="10000"
        step="0.01"
        value={paymentAmountDollars}
        disabled={disableLockedPublishedFields}
        onChange={(event) => {
          const nextValue = sanitizePaymentInput(event.target.value);
          if (nextValue !== null) {
            setPaymentAmountDollars(nextValue);
          }
        }}
        className={`w-full rounded-md border border-myteengig-border px-3 py-2 text-sm outline-none focus:border-myteengig-primary ${lockedFieldClassName}`}
        placeholder={copy.paymentPlaceholder}
      />
	      <p className="mt-1 text-xs text-myteengig-text-muted">{paymentHelperText}</p>
	      {copy.paymentStaticHelperText ? (
	        <p className="mt-1 text-xs text-myteengig-text-muted">
	          {copy.paymentStaticHelperText}
	        </p>
	      ) : null}
      {paymentPricing !== null && paymentPricing.teenPayoutPerSeatCents >= 100 ? (
        <div className="mt-3 rounded-xl border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-muted">
          <p>
            Estimated total due once fully staffed:{" "}
            <span className="font-semibold text-myteengig-text-primary">
              {formatUsdFromCents(estimatedTotalDueCents)}
            </span>
          </p>
          <p className="mt-1 text-xs leading-5">
            Based on {formatUsdFromCents(paymentPricing.posterPaymentPerTeenCents)} per teen x {teensRequired}.
          </p>
        </div>
      ) : null}
	    </div>
  ) : null;
  const volunteerToggle = canCreateVolunteerGig ? (
    <label className={`flex items-start gap-3 rounded-2xl border border-myteengig-border bg-myteengig-background/60 px-4 py-3 ${restrictPublishedFields ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={isVolunteerGig}
        disabled={disableLockedPublishedFields}
        onChange={(event) => {
          const checked = event.target.checked;
          setIsVolunteerGig(checked);
          if (!checked) {
            setCommunityServiceHoursEligible(false);
            setCommunityServiceProofDetails("");
          }
          if (checked) {
            setPaymentAmountDollars("");
          }
        }}
        className="mt-1"
      />
      <span>
        <span className="block text-sm font-semibold text-myteengig-text-primary">Volunteer opportunity</span>
        <span className="mt-1 block text-xs leading-5 text-myteengig-text-muted">
          {copy.volunteerPublishedMessage}
        </span>
      </span>
	    </label>
	  ) : null;
  const durationField = (
    <div>
      <label className="mb-1 block text-sm font-medium text-myteengig-text-primary">{durationLabel}</label>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {durationOptions.map((option) => {
          const selected = durationPreset === option.value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={false}
              onClick={() => {
                setDurationPreset(option.value);
                if (typeof option.minutes === "number") {
                  applyEstimatedDuration(option.minutes);
                }
              }}
              className={[
                getSelectionCardClassName(selected, "center"),
                "min-h-[72px] px-3 py-3",
              ].join(" ")}
              aria-pressed={selected}
            >
              <span className="block text-sm font-semibold text-myteengig-text-primary">{option.label}</span>
              {option.description ? (
                <span className="mt-1 block text-xs text-myteengig-text-muted">{option.description}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      {durationPreset === "CUSTOM" ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="gig-estimated-hours" className="mb-1 block text-xs font-medium text-myteengig-text-muted">
              Hours
            </label>
            <input
              id="gig-estimated-hours"
              type="number"
              required
              min="0"
              max="24"
              step="1"
              value={estimatedHours}
              onChange={(event) => setEstimatedHours(event.target.value)}
              className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              placeholder="1"
            />
          </div>
          <div>
            <label htmlFor="gig-estimated-minutes" className="mb-1 block text-xs font-medium text-myteengig-text-muted">
              Minutes
            </label>
            <div className="relative">
              <select
                id="gig-estimated-minutes"
                required
                value={estimatedMinutes}
                onChange={(event) => setEstimatedMinutes(event.target.value)}
                className="h-[42px] w-full appearance-none rounded-md border border-myteengig-border bg-white px-3 pr-10 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
              >
                {customMinuteOptions.map((option) => (
                  <option key={option} value={option}>
                    {option.padStart(2, "0")}
                  </option>
                ))}
              </select>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-myteengig-text-muted"
              >
                ▼
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
  const hourlyEquivalentPreview = teenHourlyEquivalentCents !== null && estimatedDurationMinutesForPreview !== null ? (
    <div className="rounded-2xl border border-myteengig-primary/20 bg-white px-4 py-3 text-sm text-myteengig-text-muted">
      <p className="font-semibold text-myteengig-text-primary">Estimated pay breakdown</p>
      <p className="mt-2">
        Based on an estimated duration of{" "}
        <span className="font-semibold text-myteengig-text-primary">
          {formatEstimatedDuration(estimatedDurationMinutesForPreview)}
        </span>
        , each teen payout works out to about{" "}
        <span className="font-semibold text-myteengig-text-primary">
          {formatUsdFromCents(teenHourlyEquivalentCents)}/hour
        </span>{" "}
        after MyTeenGig fees.
      </p>
      <p className="mt-2 text-xs leading-5">
        Checkout uses one combined total after every requested spot is selected.
      </p>
    </div>
  ) : null;
  const communityServiceHoursField = isVolunteerGig ? (
    <div className="space-y-3 rounded-2xl border border-myteengig-border bg-white px-4 py-3">
      <label className="flex items-start gap-3 text-sm font-medium text-myteengig-text-primary">
        <input
          type="checkbox"
          checked={communityServiceHoursEligible}
          disabled={disableLockedPublishedFields}
          onChange={(event) => setCommunityServiceHoursEligible(event.target.checked)}
          className="mt-1"
        />
        Community service hours are available for this volunteer gig
      </label>
      {communityServiceHoursEligible ? (
        <div>
          <label htmlFor="gig-community-service-proof" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            How will you verify the hours? <span aria-hidden="true">*</span>
          </label>
          <textarea
            id="gig-community-service-proof"
            required
            maxLength={1000}
            rows={3}
            value={communityServiceProofDetails}
            disabled={disableLockedPublishedFields}
            onChange={(event) => setCommunityServiceProofDetails(event.target.value)}
            className="w-full rounded-md border border-myteengig-border px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
            placeholder="I will sign the teen's school service-hours form after the gig."
          />
          <p className="mt-1 text-xs text-myteengig-text-muted">Name the form or documentation you will provide. Teens will see these details.</p>
        </div>
      ) : null}
    </div>
  ) : null;

  const showBasicsStep = !isGuidedCreateFlow || activeFlowStep === "BASICS";
  const showPaymentStep = !isGuidedCreateFlow || activeFlowStep === "PAYMENT";
  const showWorkStep = !isGuidedCreateFlow || activeFlowStep === "WORK";
  const showTimingStep = !isGuidedCreateFlow || activeFlowStep === "TIMING";
  const currentFlowStepIndex = Math.max(
    0,
    createGigFlowSteps.findIndex((step) => step.value === activeFlowStep),
  );
  const currentFlowStepLabel = createGigFlowSteps[currentFlowStepIndex]?.label ?? createGigFlowSteps[0].label;
  const progressPercent = Math.round(((currentFlowStepIndex + 1) / createGigFlowSteps.length) * 100);

	  return (
	    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle} panelClassName="max-w-3xl">
	      <form onSubmit={(event) => void handleCreateGig(event)} className="space-y-4">
        {isGuidedCreateFlow ? (
          <div className="rounded-xl border border-myteengig-border bg-white px-3 py-3 shadow-sm sm:px-4">
            <div className="mb-2 flex items-center justify-between gap-3 text-xs font-semibold text-myteengig-text-muted">
              <span>Step {currentFlowStepIndex + 1} of {createGigFlowSteps.length}</span>
              <span className="text-myteengig-primary">{currentFlowStepLabel}</span>
            </div>
            <div
              aria-label="Create gig progress"
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={progressPercent}
              aria-valuetext={`Step ${currentFlowStepIndex + 1} of ${createGigFlowSteps.length}: ${currentFlowStepLabel}`}
              className="h-2 overflow-hidden rounded-full bg-myteengig-secondary"
              role="progressbar"
            >
              <div
                className="h-full rounded-full bg-myteengig-primary transition-all duration-300 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <ol className="mt-3 grid grid-cols-4 gap-2 text-center text-[0.68rem] font-medium leading-tight text-myteengig-text-muted sm:text-xs">
              {createGigFlowSteps.map((step, index) => {
                const isCurrent = step.value === activeFlowStep;
                const isComplete = index < currentFlowStepIndex;
                return (
                  <li
                    key={step.value}
                    aria-current={isCurrent ? "step" : undefined}
                    className={
                      isCurrent
                        ? "text-myteengig-primary"
                        : isComplete
                          ? "text-myteengig-text-primary"
                          : "text-myteengig-text-muted"
                    }
                  >
                    <span
                      aria-hidden="true"
                      className={`mx-auto mb-1 block h-2 w-2 rounded-full ${
                        isCurrent || isComplete ? "bg-myteengig-primary" : "bg-myteengig-border"
                      }`}
                    />
                    {step.label}
                  </li>
                );
              })}
            </ol>
	          </div>
	        ) : null}

	      {restrictPublishedFields ? (
	        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Teens are already requesting this gig. You can update the description, equipment details, ZIP, timing details, and estimated duration here. Title, category, crew size, pay, volunteer status, and photos stay locked until the active requests are cleared.
	        </div>
	      ) : null}
        {showBasicsStep ? (
          <>
	        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Combobox
              id="gig-category"
              label={copy.categoryLabel}
              value={category}
              required
	              maxLength={60}
	              options={gigCategoryOptions}
		              onChange={(value) => {
		                if (!restrictPublishedFields) {
		                  setCategory(value);
		                }
		              }}
              placeholder={copy.categoryPlaceholder}
              helperText={copy.categoryHelperText}
              disabled={disableLockedPublishedFields}
            />
          </div>

          <div>
            <label htmlFor="gig-title" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
              {copy.titleLabel}
            </label>
            <input
              id="gig-title"
              type="text"
              required
              maxLength={120}
              value={title}
              disabled={disableLockedPublishedFields}
              onChange={(event) => setTitle(event.target.value)}
	              className={`w-full rounded-md border border-myteengig-border px-3 py-2 text-sm outline-none focus:border-myteengig-primary ${lockedFieldClassName}`}
	              placeholder={copy.titlePlaceholder}
	            />
	            <p className="mt-1 text-xs text-myteengig-text-muted">{copy.titleHelperText}</p>
		          </div>
		        </div>

		        <div className="max-w-md">
		          <div>
		            <div className="mb-1 flex items-center justify-center gap-2 text-center">
		              <span className="text-sm font-medium text-myteengig-text-primary">{copy.locationLabel}</span>
	              <InfoTooltip
	                placement="bottom-center"
	                message={copy.locationTooltip}
	              />
            </div>
            <div className="space-y-3">
              <div
                role="group"
                aria-label="Gig location type"
                className="grid grid-cols-2 overflow-hidden rounded-lg border border-myteengig-border bg-myteengig-background/60 p-1"
              >
                {([
                  { value: "IN_PERSON", label: "In Person" },
                  { value: "REMOTE", label: "Remote" },
                ] as const).map((option) => {
                  const isSelected = locationType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => handleLocationTypeChange(option.value)}
                      className={`rounded-md px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-myteengig-primary/30 ${
                        isSelected
                          ? "bg-myteengig-primary text-white shadow-sm"
                          : "text-myteengig-text-muted hover:bg-white hover:text-myteengig-text-primary"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {locationType === "IN_PERSON" ? (
                showGigLocationEditor ? (
                  <UsLocationFields
                    idPrefix="gig"
                    city={city}
                    state={state}
                    zip={zip}
                    required
                    labels="sr-only"
                    cityPlaceholder="City"
                    zipPlaceholder="ZIP"
                    onCityChange={(value) => {
                      setCity(value);
                      setIsEditingGigLocation(true);
                    }}
                    onStateChange={(value) => {
                      setState(value);
                      setIsEditingGigLocation(true);
                    }}
	                    onZipChange={(value) => {
	                      setZip(value);
	                      setIsEditingGigLocation(true);
	                    }}
	                    onZipDetailsChange={handleZipDetailsChange}
	                  />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsEditingGigLocation(true)}
                      className="flex w-full items-center gap-3 rounded-md border border-myteengig-border bg-white px-3 py-2 text-left transition hover:border-myteengig-primary/60 hover:bg-myteengig-background/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-myteengig-primary/35"
                      aria-label={`Edit gig location. Current location: ${fullGigLocation}`}
                    >
                      <img src={locationPinIcon} alt="" aria-hidden="true" className="h-5 w-5 shrink-0" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-myteengig-text-primary">{gigLocationSummary}</span>
                      </span>
                    </button>
                    <p className="mt-1 text-xs text-myteengig-text-muted">{copy.locationEditHint}</p>
                  </>
                )
              ) : (
                <div className="rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm font-semibold text-myteengig-text-primary">
                  Remote
                </div>
              )}
            </div>
          </div>

	        </div>

        <fieldset className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
          <legend className="px-1 text-sm font-semibold text-myteengig-text-primary">What kind of gig are you posting?</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            {([
              { value: "ONE_TIME", label: "One-time gig", description: "A single job or short-term task." },
              { value: "RECURRING_INTRO", label: "Recurring opportunity", description: "Start with one paid introductory gig, then discuss future work directly." },
            ] as const).map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={opportunityType === option.value}
                disabled={restrictPublishedFields}
                onClick={() => setOpportunityType(option.value)}
                className={getSelectionCardClassName(opportunityType === option.value)}
              >
                <span className="block text-sm font-semibold text-myteengig-text-primary">{option.label}</span>
                <span className="mt-1 block text-xs text-myteengig-text-muted">{option.description}</span>
              </button>
            ))}
          </div>
          {isRecurringIntro ? (
            <p className="mt-3 text-xs leading-5 text-myteengig-text-muted">
              MyTeenGig handles the first gig only. You and the teen can arrange future work directly after it is complete.
            </p>
          ) : null}
        </fieldset>
	          </>
	        ) : null}

        {showPaymentStep ? (
          <section className="space-y-4 rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
	            <div>
	              <h3 className="text-lg font-semibold text-myteengig-text-primary">Payment</h3>
	              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
	                {isRecurringIntro
                  ? "Set how many teens you need and how much you want to pay each teen for the first gig. Future work is not included. Payment is "
                  : "Set how many teens you need and how much you want to pay each teen. Gigs are only paid "}
	                <strong className="font-semibold text-myteengig-text-primary">
                  {isRecurringIntro ? "a fixed amount, not hourly payment." : "as a fixed amount, not hourly payment."}
                </strong>
	              </p>
	            </div>
            {teensRequiredField}
            {durationField}
	            <div className={canCreateVolunteerGig ? "space-y-3" : ""}>
	              {paymentField}
	              {volunteerToggle}
	              {communityServiceHoursField}
	            </div>
            {hourlyEquivalentPreview}
	          </section>
	        ) : null}

        {showWorkStep ? (
          <>
        <div>
          <label htmlFor="gig-description" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
            {isRecurringIntro ? "Describe the introductory gig" : copy.descriptionLabel}
          </label>
          <textarea
            id="gig-description"
            required
            rows={4}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              if (createError === GIG_DESCRIPTION_CONTACT_INFO_BLOCK_MESSAGE) {
                setCreateError(null);
              }
            }}
            aria-invalid={descriptionContainsBlockedContactInfo}
            className={`w-full rounded-md border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary ${
              descriptionContainsBlockedContactInfo ? "border-red-500" : "border-myteengig-border"
            }`}
            placeholder={descriptionPlaceholder}
          />
          <p className="mt-1 text-xs text-myteengig-text-muted">
            {descriptionHelperText}
          </p>
          <p className="mt-3 rounded-xl border border-myteengig-border bg-myteengig-background px-3 py-2 text-xs leading-relaxed text-myteengig-text-muted">
            {GIG_DESCRIPTION_CONTACT_INFO_NOTICE}{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-myteengig-primary underline underline-offset-2 hover:text-myteengig-primary-hover"
            >
              Review the Terms
            </a>
            .
          </p>
          {descriptionContainsBlockedContactInfo ? (
            <p className="mt-2 text-sm text-red-600">{GIG_DESCRIPTION_CONTACT_INFO_BLOCK_MESSAGE}</p>
          ) : null}
          {copy.descriptionChecklistTitle && copy.descriptionChecklistText ? (
            <div className="mt-3 rounded-2xl border border-myteengig-border bg-myteengig-background/60 px-4 py-3 text-sm text-myteengig-text-muted">
              <span className="font-semibold text-myteengig-text-primary">{copy.descriptionChecklistTitle}</span>{" "}
              {copy.descriptionChecklistText}
            </div>
          ) : null}
	        </div>

          <div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={requiresEquipment}
                onChange={(event) => setRequiresEquipment(event.target.checked)}
                className="h-4 w-4 rounded border-myteengig-border text-myteengig-primary focus:ring-myteengig-primary"
              />
              <span className="text-sm font-semibold text-myteengig-text-primary">{copy.equipmentToggleLabel}</span>
            </label>
            {requiresEquipment ? (
              <div className="mt-3">
                <label htmlFor="gig-equipment-description" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                  {copy.equipmentDescriptionLabel}
                </label>
                <textarea
                  id="gig-equipment-description"
                  required
                  rows={3}
                  maxLength={1000}
                  value={equipmentDescription}
                  onChange={(event) => setEquipmentDescription(event.target.value)}
                  className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder={copy.equipmentDescriptionPlaceholder}
                />
              </div>
            ) : null}
          </div>

	        {isVolunteerGig ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            {copy.volunteerPublishedMessage}
          </div>
        ) : null}

        <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-3">
          <label
            htmlFor="gig-images"
            className={`flex items-center justify-center gap-2 rounded-2xl border border-dashed border-myteengig-border px-4 py-3 text-center transition ${disableLockedPublishedFields ? "cursor-not-allowed bg-slate-100 text-slate-500" : "cursor-pointer bg-white hover:border-myteengig-primary hover:bg-myteengig-background/60"}`}
          >
            <img src={cameraIcon} alt="" aria-hidden="true" className="h-5 w-5 text-myteengig-primary" />
            <span className="text-sm font-semibold text-myteengig-text-primary">{copy.photosLabel}</span>
          </label>
          <input
            id="gig-images"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            className="sr-only"
            disabled={disableLockedPublishedFields}
            onChange={onImageFilesSelected}
          />
          {images.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {images.map((image, index) => (
                <div key={`${image.name}-${index}`} className="inline-flex max-w-full items-center gap-2 rounded-full border border-myteengig-border bg-white px-3 py-1.5 text-xs">
                  <span className="max-w-[12rem] truncate font-medium text-myteengig-text-primary">{image.name}</span>
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="font-semibold text-red-600 transition-colors hover:text-red-700"
                    aria-label={`Remove ${image.name}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {copy.photosHelperText ? <p className="mt-3 text-xs text-myteengig-text-muted">{copy.photosHelperText}</p> : null}
        </div>
          </>
        ) : null}

        {showTimingStep ? (
          <>
      <div className="space-y-4 rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
        <div>
          <p className="text-sm font-medium text-myteengig-text-primary">{scheduleLabel}</p>
          {copy.scheduleHelperText ? <p className="mt-1 text-xs text-myteengig-text-muted">{copy.scheduleHelperText}</p> : null}
            <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {dueDateOptions.map((option) => {
                const selected = dueDatePreset === option.value;
                return (
                  <div key={option.value} className="relative">
                    <button
                      type="button"
                      disabled={false}
                      onClick={() => {
                        setDueDatePreset(option.value);
                        if (option.value !== "CUSTOM") {
                          setStartTime("");
                          setEndTime("");
                        }
                      }}
	                      className={`${getSelectionCardClassName(selected, "center")} flex min-h-[3.25rem] w-full items-center justify-center`}
	                      aria-pressed={selected}
	                    >
	                      <span className="whitespace-nowrap text-center text-sm font-semibold text-myteengig-text-primary">{option.label}</span>
	                    </button>
	                  </div>
	                );
	              })}
            </div>
          </div>

	          {dueDatePreset === "FLEXIBLE" ? (
	            <div>
	              <div className="mb-1 flex items-center gap-2">
	                <label htmlFor="gig-flexible-timing-details" className="text-sm font-medium text-myteengig-text-primary">
	                  {copy.flexibleTimingLabel}
	                </label>
	                <InfoTooltip
	                  placement="top-left"
	                  label="What does I'm flexible mean?"
	                  message={copy.flexibleTimingTooltip}
	                />
	              </div>
              <textarea
                id="gig-flexible-timing-details"
                required
                rows={3}
                maxLength={240}
                value={flexibleTimingDetails}
                onChange={(event) => setFlexibleTimingDetails(event.target.value)}
                className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                placeholder={copy.flexibleTimingPlaceholder}
              />
            </div>
          ) : (
            <div className="space-y-4">
	              {dueDatePreset === "CUSTOM" ? (
	                <div className="space-y-4 rounded-2xl border border-myteengig-border bg-white p-4">
	                  <div>
	                    <p className="text-sm font-medium text-myteengig-text-primary">{copy.customScheduleLabel}</p>
	                  </div>

	                  <div className="grid gap-3 sm:grid-cols-2">
	                    {customScheduleDateModeOptions.map((option) => {
	                      const selected = customScheduleDateMode === option.value;
	                      return (
	                        <button
	                          key={option.value}
	                          type="button"
	                          onClick={() => setCustomScheduleDateMode(option.value)}
	                          className={getSelectionCardClassName(selected)}
	                          aria-pressed={selected}
	                        >
	                          <span className="block text-sm font-semibold text-myteengig-text-primary">{option.label}</span>
	                          <span className="mt-1 block text-xs text-myteengig-text-muted">{option.description}</span>
	                        </button>
	                      );
	                    })}
	                  </div>

	                  {customScheduleDateMode === "SPECIFIC_DATE" ? (
	                    <div>
	                      <label htmlFor="gig-specific-date" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
	                        Date
	                      </label>
	                      <input
	                        id="gig-specific-date"
	                        type="date"
	                        required
	                        value={startTime}
	                        min={minimumStartDate}
	                        onChange={(event) => setStartTime(event.target.value)}
	                        className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
	                      />
	                    </div>
	                  ) : (
	                    <div className="grid gap-4 sm:grid-cols-2">
	                      <div>
	                        <label htmlFor="gig-start-time" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
	                          Start date
	                        </label>
	                        <input
	                          id="gig-start-time"
	                          type="date"
	                          required
	                          value={startTime}
	                          min={minimumStartDate}
	                          onChange={(event) => setStartTime(event.target.value)}
	                          className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
	                        />
	                      </div>
	                      <div>
	                        <label htmlFor="gig-end-time" className="mb-1 block text-sm font-medium text-myteengig-text-primary">
	                          End date
	                        </label>
	                        <input
	                          id="gig-end-time"
	                          type="date"
	                          required
	                          value={endTime}
	                          min={startTime || minimumStartDate}
	                          onChange={(event) => setEndTime(event.target.value)}
	                          className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
	                        />
	                      </div>
	                    </div>
	                  )}
	                </div>
	              ) : null}

	              <div>
	                <div className="flex items-center justify-center gap-2 text-center">
	                  <p className="text-center text-sm font-medium text-myteengig-text-primary">{copy.preferredTimeLabel}</p>
	                  <InfoTooltip
	                    placement="top-right"
	                    label="What is this time window?"
	                    message={copy.preferredTimeTooltip}
	                  />
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {preferredTimeOptions.map((option) => {
                    const selected = preferredTimePreset === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={false}
                        onClick={() => {
                          setPreferredTimePreset(option.value);
                          if (option.value !== "CUSTOM") {
                            setCustomPreferredStartTime("");
                            setCustomPreferredEndTime("");
                          }
                        }}
                        className={getSelectionCardClassName(selected)}
                        aria-pressed={selected}
                      >
                        <div className="flex items-center gap-3">
                          <div className="rounded-full bg-myteengig-primary/10 p-2">
                            <TimeSlotIcon preset={option.value} />
                          </div>
                          <div>
                            <span className="block text-sm font-semibold text-myteengig-text-primary">{option.label}</span>
                            <span className="mt-1 block text-xs text-myteengig-text-muted">{option.description}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {preferredTimePreset === "CUSTOM" ? (
                  <div className="mt-3 space-y-3 rounded-2xl border border-myteengig-border bg-white p-4">
                    <p className="text-sm text-myteengig-text-muted">Anytime Between</p>
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-end">
                      <div>
                        <label htmlFor="gig-custom-preferred-start-time" className="sr-only">
                          Start time
                        </label>
                        <input
                          id="gig-custom-preferred-start-time"
                          type="time"
                          required
                          step={900}
                          value={customPreferredStartTime}
                          onChange={(event) => setCustomPreferredStartTime(event.target.value)}
                          className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                        />
                      </div>
                      <span className="pb-2 text-sm font-medium text-myteengig-text-primary">To</span>
                      <div>
                        <label htmlFor="gig-custom-preferred-end-time" className="sr-only">
                          End time
                        </label>
                        <input
                          id="gig-custom-preferred-end-time"
                          type="time"
                          required
                          step={900}
                          value={customPreferredEndTime}
                          onChange={(event) => setCustomPreferredEndTime(event.target.value)}
                          className="w-full rounded-md border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                        />
                      </div>
                    </div>
                    {customPreferredTime.trim().length > 0
                    && !customPreferredStartTime
                    && !customPreferredEndTime ? (
                      <p className="text-xs text-myteengig-text-muted">
                        Previously saved custom time: <span className="font-semibold text-myteengig-text-primary">{customPreferredTime}</span>
                      </p>
                      ) : null}
                  </div>
                ) : null}
                <div className="mt-3 rounded-2xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-muted">
	                  {dueDatePreset === "CUSTOM" ? (
	                    <>
	                      {customScheduleDateMode === "SPECIFIC_DATE" ? (
	                        <>
	                          {copy.scheduleSummaryCustomSpecificLead}{" "}
	                          <span className="font-semibold text-myteengig-text-primary">{startTime || "your date"}</span>
	                        </>
	                      ) : (
	                        <>
	                          {copy.scheduleSummaryCustomLead}{" "}
	                          <span className="font-semibold text-myteengig-text-primary">{startTime || "your start date"}</span>
	                          {" "}and{" "}
	                          <span className="font-semibold text-myteengig-text-primary">{endTime || "your end date"}</span>
	                        </>
	                      )}
	                      {" "}and that you prefer{" "}
	                      <span className="font-semibold text-myteengig-text-primary">
                        {getPreferredTimeLabel(
                          preferredTimePreset,
                          preferredTimePreset === "CUSTOM"
                            ? buildCustomPreferredTimeWindowLabel(customPreferredStartTime, customPreferredEndTime) || customPreferredTime
                            : customPreferredTime,
                        ) || "a custom time window"}
                      </span>.
                    </>
                  ) : (
                    <>
                      {copy.scheduleSummaryPresetLead}{" "}
                      <span className="font-semibold text-myteengig-text-primary">{getDueDateLabel(dueDatePreset as SimpleDueDatePreset).toLowerCase()}</span>
                      {" "}and that you prefer{" "}
                      <span className="font-semibold text-myteengig-text-primary">
                        {getPreferredTimeLabel(
                          preferredTimePreset,
                          preferredTimePreset === "CUSTOM"
                            ? buildCustomPreferredTimeWindowLabel(customPreferredStartTime, customPreferredEndTime) || customPreferredTime
                            : customPreferredTime,
                        ) || "a custom time window"}
                      </span>.
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

          </>
        ) : null}
        {createError ? (
          <p role="alert" className="text-sm text-red-700">
            {createError}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {isGuidedCreateFlow && activeFlowStep !== "BASICS" ? (
            <Button type="button" variant="outline" onClick={handlePreviousFlowStep} disabled={isSubmitting}>
              Back
            </Button>
          ) : null}
          {isGuidedCreateFlow && activeFlowStep !== "TIMING" ? (
            <Button type="button" onClick={handleNextFlowStep} disabled={isSubmitting}>
              Continue
            </Button>
          ) : null}
          {(!isGuidedCreateFlow || activeFlowStep === "TIMING") && showSaveDraft ? (
            <Button
              type="submit"
              variant="outline"
              disabled={isSubmitting || descriptionContainsBlockedContactInfo}
              onClick={() => setSubmitMode("DRAFT")}
            >
              {isSubmitting && submitMode === "DRAFT" ? draftingLabel : draftLabel}
            </Button>
          ) : null}
          {!isGuidedCreateFlow || activeFlowStep === "TIMING" ? (
            <Button type="submit" disabled={isSubmitting || descriptionContainsBlockedContactInfo} onClick={() => setSubmitMode("PUBLISHED")}>
              {isSubmitting && submitMode === "PUBLISHED" ? publishingLabel : publishLabel}
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
};

export default CreateGigModal;
